import { useSelector, useDispatch } from "react-redux";
import {
  setFirstName,
  setLastName,
  setGpid,
  setManager,
  setJobDescription,
  setSelectedStatus,
  clearValidationError,
} from "../../../store/slices/employeeUpdatesSlice";
import type { AppDispatch } from "../../../store";

/**
 * Custom hook to handle employee form state and actions with Redux.
 * Centralizes form field management and validation handling.
 */
export const useEmployeeForm = () => {
  const dispatch = useDispatch<AppDispatch>();

  // Extract only the form-related state from Redux
  const {
    firstName,
    lastName,
    gpid,
    manager,
    jobDescription,
    selectedStatus,
    validationErrors,
  } = useSelector((state: any) => state.employeeUpdates);

  // Form field handlers
  const handleFirstNameChange = (value: string) => {
    dispatch(setFirstName(value));
    if (validationErrors?.firstName) {
      dispatch(clearValidationError("firstName"));
    }
  };

  const handleLastNameChange = (value: string) => {
    dispatch(setLastName(value));
    if (validationErrors?.lastName) {
      dispatch(clearValidationError("lastName"));
    }
  };

  const handleGpidChange = (value: string) => {
    dispatch(setGpid(value));
    if (validationErrors?.gpid) {
      dispatch(clearValidationError("gpid"));
    }
  };

  const handleManagerChange = (value: string) => {
    dispatch(setManager(value));
    if (validationErrors?.manager) {
      dispatch(clearValidationError("manager"));
    }
  };

  const handleJobDescriptionChange = (value: string) => {
    dispatch(setJobDescription(value));
    if (validationErrors?.jobDescription) {
      dispatch(clearValidationError("jobDescription"));
    }
  };

  const handleStatusChange = (value: any) => {
    dispatch(setSelectedStatus(value));
    if (validationErrors?.selectedStatus) {
      dispatch(clearValidationError("selectedStatus"));
    }
  };

  const handleClearValidationError = (field: string) => {
    dispatch(clearValidationError(field));
  };

  // Return form state values, handlers, and validation errors
  return {
    formState: {
      firstName,
      lastName,
      gpid,
      manager,
      jobDescription,
      selectedStatus,
    },
    handlers: {
      handleFirstNameChange,
      handleLastNameChange,
      handleGpidChange,
      handleManagerChange,
      handleJobDescriptionChange,
      handleStatusChange,
      handleClearValidationError,
    },
    validationErrors,
  };
};
